import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Cart drawer upsell (fixed collection).
 *
 * Shows every product from the chosen collection. Products are NOT filtered by
 * what's already in the cart and cards are NOT removed after being added — the
 * card stays put, and clicking "Add" again simply adds another unit.
 *
 * Adding mirrors the theme's own add-to-cart flow (see product-form.js): it
 * dispatches a real `CartLinesUpdateEvent` carrying a deferred `.promise`, POSTs
 * to /cart/add with a `sections` param, then resolves the promise with the
 * freshly rendered sections + cart. The drawer (`cart-items-component`), the
 * cart icon and the header all listen for that event, so the added product
 * appears in the cart list and the count/total update without a page reload.
 *
 * Clicks are handled by delegation on the container, so the wiring survives any
 * cart re-render/morph that swaps the inner card DOM.
 */
class CartRecommendations extends HTMLElement {
  /** @type {boolean} */
  #wired = false;

  connectedCallback() {
    this.addUrl = this.dataset.addUrl || '/cart/add.js';
    this.cartUrl = this.dataset.cartUrl || '/cart.js';

    // Guard against re-wiring the same element instance after a morph re-connects it.
    // (A fresh element inserted by a morph starts with #wired = false and wires up.)
    if (this.#wired) return;
    this.#wired = true;
    this.addEventListener('click', this.#onClick);
    // Delegado como o clique, pelo mesmo motivo: o morph do carrinho troca o
    // DOM dos cards e um listener preso ao <select> se perderia.
    this.addEventListener('change', this.#onChange);
  }

  /**
   * Picking a size fills the button's variant id and unlocks it. Back to the
   * empty option and the button locks again, so an add can never carry a size
   * the customer did not choose.
   * @param {Event} event
   */
  #onChange = (event) => {
    const target = event.target;
    const select = target instanceof Element ? target.closest('[data-rec-size]') : null;
    if (!select || !this.contains(select)) return;

    const card = select.closest('.cart-rec');
    const btn = card && card.querySelector('[data-rec-add]');
    if (!(btn instanceof HTMLButtonElement)) return;

    const variantId = /** @type {HTMLSelectElement} */ (select).value;
    btn.dataset.variantId = variantId;
    btn.disabled = !variantId;
    if (variantId) select.removeAttribute('data-missing');
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    const btn = target instanceof Element ? target.closest('[data-rec-add]') : null;
    if (!btn || !this.contains(btn)) return;
    event.preventDefault();
    this.#add(/** @type {HTMLElement} */ (btn));
  };

  /**
   * Section IDs of every cart-items-component currently on the page (the drawer,
   * and the cart page if present), so the add response re-renders each of them.
   * @returns {string[]}
   */
  #sectionIds() {
    const ids = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
    });
    return ids;
  }

  /**
   * @param {HTMLElement} btn - The clicked add button.
   */
  async #add(btn) {
    const variantId = btn.dataset.variantId;
    // Ignore while a previous add for this button is still in flight.
    if (!variantId || btn.getAttribute('aria-busy') === 'true') return;

    btn.setAttribute('aria-busy', 'true');
    btn.setAttribute('disabled', '');

    const sectionIds = this.#sectionIds();
    const deferred = CartLinesUpdateEvent.createPromise();

    // Announce the add up-front (mirrors product-form). All cart UIs listen for
    // this event and refresh once the promise resolves with the sections below.
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const body = new FormData();
      body.append('id', variantId);
      body.append('quantity', '1');
      if (sectionIds.length) body.append('sections', sectionIds.join(','));
      body.append('sections_url', window.location.pathname);

      const res = await fetch(this.addUrl, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
        body,
      });
      const data = await res.json();
      // On failure /cart/add.js returns an error object carrying a `status` field.
      if (data && data.status) throw new Error(data.description || data.message || 'add failed');

      // Fetch the full cart for accurate totals (the add response is just the line).
      const ajaxCart = await fetch(this.cartUrl, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((r) => r.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          source: 'cart-recommendations',
          sections: data.sections,
          itemCount: 1,
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
    } finally {
      // Card stays available so the shopper can add another unit right away.
      btn.removeAttribute('aria-busy');
      btn.removeAttribute('disabled');
    }
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}
